/**
 * Modelo de dominio: Aseguradora (Cliente).
 * 
 * Representa una aseguradora activa del sistema.
 * 
 * @legacy FilterProgramarServiciosViewImpl.java (dropdown Aseguradora)
 * @table TPSC_CLIENTES con JOIN a TPSC_PERSONAS
 */
export interface Aseguradora {
    /**
     * Código único del cliente (PK).
     * Campo: NMCONSECUTIVO
     */
    nmconsecutivo: number;
    
    /**
     * Código de persona asociada al cliente.
     * Campo: CDPERSONA (FK a TPSC_PERSONAS)
     */
    cdpersona: number;
    
    /**
     * Código del tipo de identificación (ej: N, C, E).
     * Campo: CDTIPO_IDENTIFICACION
     */
    cdtipoIdentificacion?: string;
    
    /**
     * Descripción del tipo de identificación (ej: NIT, CC).
     * Campo: DSTIPO_IDENTIFICACION
     */
    tipoIdentificacion?: string;
    
    /**
     * Número de identificación.
     * Campo: NMIDENTIFICACION
     */
    numeroIdentificacion: string;
    
    /**
     * Nombre comercial de la aseguradora.
     * Campo: DSNOMBRE_COMERCIAL (de TPSC_PERSONAS)
     */
    nombreComercial?: string;
    
    /**
     * Primer nombre (si no tiene nombre comercial).
     * Campo: DSNOMBRE1 (de TPSC_PERSONAS)
     */
    primerNombre?: string;
    
    /**
     * Primer apellido.
     * Campo: DSAPELLIDO1 (de TPSC_PERSONAS)
     */
    primerApellido?: string;
    
    /**
     * Indicador de activo (S/N).
     * Campo: SNACTIVO
     */
    snactivo?: string;
}

/**
 * Filtro para búsqueda de aseguradoras.
 */
export interface AseguradoraFiltro {
    /** Tipo de identificación */
    tipoIdentificacion?: string;
    /** Número de identificación (coincidencia exacta con NMIDENTIFICACION) */
    numeroIdentificacion?: string;
    /** Nombre comercial (búsqueda parcial solo sobre DSNOMBRE_COMERCIAL). Solo con tipo NIT. */
    nombreComercial?: string;
    /** Primer nombre (búsqueda parcial, DSNOMBRE1). Solo con tipo distinto de NIT. */
    nombre1?: string;
    /** Segundo nombre (búsqueda parcial, DSNOMBRE2). Solo con tipo distinto de NIT. */
    nombre2?: string;
    /** Primer apellido (búsqueda parcial, DSAPELLIDO1). Solo con tipo distinto de NIT. */
    apellido1?: string;
    /** Segundo apellido (búsqueda parcial, DSAPELLIDO2). Solo con tipo distinto de NIT. */
    apellido2?: string;
    /** Filtro activo: 'S' = Si, 'N' = No, undefined = Todos */
    activo?: string;
}

/**
 * Respuesta paginada de aseguradoras (keyset/cursor pagination — ver skill
 * acceso-datos-oracle §7. El backend ya no acepta offset/page).
 */
export interface AseguradoraPageResponse {
    content: Aseguradora[];

    /** El backend no calcula el total real en este endpoint; puede venir ausente. */
    totalElements?: number;

    /** Cursor opaco para pedir la página siguiente (null si no hay más) */
    nextCursor: string | null;

    /** Cursor opaco para pedir la página anterior (null si ya es la primera) */
    previousCursor: string | null;

    hasNext: boolean;
    hasPrevious: boolean;
}

/**
 * Utilidad para obtener el nombre de display de una aseguradora.
 */
export function getAseguradoraNombreDisplay(aseguradora: Aseguradora): string {
    if (aseguradora.nombreComercial && aseguradora.nombreComercial.trim()) {
        return aseguradora.nombreComercial;
    }
    const parts: string[] = [];
    if (aseguradora.primerNombre) parts.push(aseguradora.primerNombre);
    if (aseguradora.primerApellido) parts.push(aseguradora.primerApellido);
    return parts.join(' ');
}
